/**
 * WalletContext.js — Context React + Provider pour EarniWalletModal.
 *
 * Expose WalletContext (pour useContext) et WalletProvider (wrappeur).
 *
 * §8.2 — Shape du context définie dans types/index.d.ts (WalletContextValue).
 * §8.7 — Aucun import Redux ; le parent reçoit onSuccess et dispatch lui-même.
 *
 * Ordre de montage dans index.tsx (§8.2) :
 *   <AppKitProvider>
 *     <WalletProvider>
 *       <Provider store={store}>
 *         <App />
 *       </Provider>
 *     </WalletProvider>
 *   </AppKitProvider>
 */
import React, { createContext, useCallback, useState } from 'react';
import { useWalletStateMachine } from '../hooks/useWalletStateMachine';

/* ─── Valeur par défaut du context (évite les erreurs si consommé hors Provider) */
export const WalletContext = createContext({
  isOpen:               false,
  openModal:            () => {},
  closeModal:           () => {},
  currentState:         'IDLE',
  dispatch:             () => {},
  currentFlow:          'connect',
  error:                null,
  clearError:           () => {},
  pendingWallet:        null,
  linkedWallets:        [],
  refreshLinkedWallets: async () => {},
  apiBaseUrl:           '',
  chainId:              43114,
});

/**
 * WalletProvider — fournit le context EarniWalletModal à l'arbre React.
 *
 * @param {object}   props
 * @param {string}   props.apiBaseUrl  — URL de base de l'API (ex: process.env.REACT_APP_SERVER)
 * @param {number}   props.chainId     — chainId Avalanche (43114 prod / 43113 Fuji)
 * @param {React.ReactNode} props.children
 */
export function WalletProvider({ apiBaseUrl, chainId, children }) {
  /* Ouverture / fermeture de la modal */
  const [isOpen, setIsOpen] = useState(false);
  const [currentFlow, setCurrentFlow] = useState('connect');

  /* Wallets liés au compte (chargés depuis GET /auth/wallets après auth) */
  const [linkedWallets, setLinkedWallets] = useState([]);

  /* Machine d'état (useReducer) — pilote toute la navigation interne */
  const { state, dispatch } = useWalletStateMachine();

  /* ── Ouverture ─────────────────────────────────────────────────────────── */
  const openModal = useCallback((flow = 'connect') => {
    setCurrentFlow(flow);
    setIsOpen(true);
    dispatch({ type: 'OPEN' });
  }, [dispatch]);

  /* ── Fermeture — remet à IDLE et efface le state machine ──────────────── */
  const closeModal = useCallback(() => {
    setIsOpen(false);
    dispatch({ type: 'RESET' });
  }, [dispatch]);

  /* ── clearError — efface l'erreur sans changer d'état ─────────────────── */
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, [dispatch]);

  /* ── refreshLinkedWallets — recharge depuis GET /auth/wallets ─────────── */
  const refreshLinkedWallets = useCallback(async () => {
    if (!apiBaseUrl) return;
    try {
      /* Import dynamique du service pour éviter une dépendance circulaire */
      const { getLinkedWallets } = await import('../services/WalletAuthService');
      const data = await getLinkedWallets(apiBaseUrl);
      setLinkedWallets(data?.wallets ?? []);
    } catch {
      /* Échec silencieux — la liste reste celle précédemment chargée */
    }
  }, [apiBaseUrl]);

  const value = {
    isOpen,
    openModal,
    closeModal,
    currentState:  state.currentState,
    dispatch,
    currentFlow,
    error:         state.error,
    clearError,
    pendingWallet: state.connectedAddress
      ? { address: state.connectedAddress, walletType: 'METAMASK', isPrimary: false, id: 0, createdAt: '' }
      : null,
    linkedWallets,
    refreshLinkedWallets,
    apiBaseUrl,
    chainId,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
}

export default WalletProvider;
